import { z } from "zod";
import { validatePayload } from "../../../api/validation";
import { fetchAirport, RawAirport } from "../../../api/airports";
import { fetchAirlines, RawAirline } from "../../../api/airlines";
import { extractAirlineData, extractAirportData } from "../../../modules/serializers/intake-funnel";
import { updateFlightDetails, updateItineraryFlights } from "../state/preEligibilityState";
import { Airline, Airport, ItineraryFlights } from "../../../modules/types/intake-funnel";
import { sendErrorReport } from "../../../../utils/reporting";

type RawFlight = z.infer<typeof rawFlightScheme>;

const rawFlightScheme = z.object({
  departureAirport: z.string().min(3),
  arrivalAirport: z.string().min(3),
  departureDate: z.string().min(1),
  airlineCode: z.string().min(2),
  flightNumber: z.string().min(1),
});

/**
 * Load itinerary flights from query params.
 * Check if the query params contain any flights data.
 * If they do, extract the flights data from the query params.
 * Validate the flights data and fetch the airports and airlines from the API.
 * Create itinerary flights from the validated data.
 * If there are multiple flights, find the connections between them.
 * Update the itinerary flights and flight details in the state.
 *
 * @returns {Promise<void>}
 */
export async function loadItineraryFlightsFromQueryParams(): Promise<void> {
  const queryParamsKeys = new URLSearchParams(window.location.search).keys();

  const areFlightsParamsPresent = Array.from(queryParamsKeys).some((key) =>
    key.startsWith("flights"),
  );

  if (areFlightsParamsPresent) {
    try {
      const { validatedRawAirlines, validatedRawAirports, validatedRawFlights } =
        await extractFlightsDataFromQueryParams();

      const itineraryFlights = createItineraryFlightsFromRawData({
        validatedRawFlights,
        validatedRawAirports,
        validatedRawAirlines,
      });

      const isMultiLeg = itineraryFlights.length > 1;

      if (isMultiLeg) {
        const connections = findConnections(itineraryFlights).map((airport) => ({ airport }));

        updateItineraryFlights(itineraryFlights);
        updateFlightDetails({
          connections,
          anyConnections: true,
        });
      } else {
        updateItineraryFlights(itineraryFlights);
      }
    } catch (error) {
      sendErrorReport(error, "Failed to extract flights data from query params");
    }
  }
}

/**
 * Extract flights data from query params.
 * Iterate over the query params and extract the flights data.
 * For each flight, validate the data and add the departure and arrival airports and airline to the respective sets.
 * Fetch the airports and airlines from the API.
 * If any of the airports or airlines are not found, throw an error.
 *
 * @returns {
 *   validatedRawFlights: RawFlight[];
 *   validatedRawAirports: RawAirport[];
 *   validatedRawAirlines: RawAirline[];
 * }
 *
 */
async function extractFlightsDataFromQueryParams(): Promise<{
  validatedRawFlights: RawFlight[];
  validatedRawAirports: RawAirport[];
  validatedRawAirlines: RawAirline[];
}> {
  const queryParamsEntries = new URLSearchParams(window.location.search).entries();

  const validatedRawFlights: RawFlight[] = [];

  const airportsToFetch = new Set<string>();
  const airlinesToFetch = new Set<string>();

  for (const [key, value] of queryParamsEntries) {
    if (key.startsWith("flights")) {
      const decodedFlightString = decodeURIComponent(value);

      const rawFlight = JSON.parse(decodedFlightString);

      const validatedRawFlight = validatePayload({
        data: rawFlight,
        schema: rawFlightScheme,
        tag: "loadItineraryFlightsFromQueryParams",
      });

      validatedRawFlights.push(validatedRawFlight);

      airportsToFetch.add(validatedRawFlight.departureAirport);
      airportsToFetch.add(validatedRawFlight.arrivalAirport);

      airlinesToFetch.add(validatedRawFlight.airlineCode);
    }
  }

  if (airportsToFetch.size === 0 || airlinesToFetch.size === 0) {
    throw Error("No airports or airlines to fetch");
  }

  const validatedRawAirports = await Promise.all(
    Array.from(airportsToFetch).map((iata) =>
      fetchAirport(iata)
        .then((rawAirport) => {
          if (!rawAirport) {
            throw Error(`Airport with IATA code ${iata} not found`);
          }
          return rawAirport;
        })
        .catch((error) => {
          sendErrorReport(error, `Failed to fetch airport with IATA code ${iata}`);
          throw error;
        }),
    ),
  );

  const validatedRawAirlines = await Promise.all(
    Array.from(airlinesToFetch).map((airlineCode) =>
      fetchAirlines(airlineCode)
        .then((rawAirlines) => rawAirlines.find((rawAirline) => rawAirline.code === airlineCode))
        .then((rawAirline) => {
          if (!rawAirline) {
            throw Error(`Airline with code ${airlineCode} not found`);
          }
          return rawAirline;
        })
        .catch((error) => {
          sendErrorReport(error, `Failed to fetch airline with code ${airlineCode}`);
          throw error;
        }),
    ),
  );

  return { validatedRawFlights, validatedRawAirports, validatedRawAirlines };
}

/**
 * Create itinerary flights from raw data.
 * Iterate over the validatedRawFlights and create an array of ItineraryFlights.
 * For each flight, find the departureAirport, arrivalAirport, and airline.
 * If any of these values are missing, throw an error.
 *
 * @param {
 *   validatedRawFlights: RawFlight[];
 *   validatedRawAirports: RawAirport[];
 *   validatedRawAirlines: RawAirline[];
 * }
 *
 * @returns {ItineraryFlights} - Itinerary flights.
 *
 */
export function createItineraryFlightsFromRawData({
  validatedRawFlights,
  validatedRawAirports,
  validatedRawAirlines,
}: {
  validatedRawFlights: RawFlight[];
  validatedRawAirports: RawAirport[];
  validatedRawAirlines: RawAirline[];
}): ItineraryFlights {
  const airports = validatedRawAirports
    .map((rawAirport) => rawAirport && extractAirportData(rawAirport))
    .filter((airport) => airport) as Airport[];

  const airlines = validatedRawAirlines
    .map((rawAirline) => rawAirline && extractAirlineData(rawAirline))
    .filter((airline) => airline) as Airline[];

  return validatedRawFlights.map((rawFlight) => {
    const departureAirport = airports.find(
      (airport) => airport.iata === rawFlight.departureAirport,
    );
    const arrivalAirport = airports.find((airport) => airport.iata === rawFlight.arrivalAirport);
    const airline = airlines.find((airline) => airline.code === rawFlight.airlineCode);
    const flightDate = new Date(rawFlight.departureDate);
    const disrupted = validatedRawFlights.length === 1;

    if (!departureAirport || !arrivalAirport || !airline) {
      throw Error(
        `Failed to find airport or airline for flight ${rawFlight.flightNumber} from ${rawFlight.departureAirport} to ${rawFlight.arrivalAirport}`,
      );
    }

    return {
      airline,
      disrupted,
      flightDate,
      arrivalAirport,
      departureAirport,
      flightNumber: rawFlight.flightNumber,
    };
  });
}

/**
 * Iterate over the arrivalAirports and check if each city is also in the departureAirports.
 * If it is, it means that the city is both a destination and a starting point for another flight,
 * thus making it a connection.
 * These values are added to the connections array.
 * @param {ItineraryFlights} itineraryFlights - Itinerary flights.
 */
function findConnections(itineraryFlights: ItineraryFlights) {
  const departureAirports = new Set<Airport>();
  const arrivalAirports = new Set<Airport>();

  itineraryFlights.forEach((itineraryFlight) => {
    if (itineraryFlight.departureAirport && itineraryFlight.arrivalAirport) {
      departureAirports.add(itineraryFlight.departureAirport);
      arrivalAirports.add(itineraryFlight.arrivalAirport);
    }
  });

  const connections: Airport[] = [];

  arrivalAirports.forEach((airport) => {
    if (departureAirports.has(airport)) {
      connections.push(airport);
    }
  });

  return connections;
}
